/**
 * Гадания, у которых нет карт: кофейная гуща, хиромантия, толкование снов и
 * два персонажа — джинн и гадалка-нэнэ.
 *
 * Устроено как `spreads.ts` и по той же причине: список — единственный
 * источник правды для клиента и сервера. Клиент не может ни удешевить формат,
 * ни прислать чужой id: цену и вид берёт отсюда сервер.
 *
 * Все пять — платные. Дневной бесплатный лимит намеренно остался за Таро:
 * он считается по раскладам (`usage_by_kind`), и подмешивать в него форматы
 * значило бы менять устройство денег ради одного экрана.
 */

import { loc, type Lang, type Loc } from './cards.ts'

export type FormatId = 'coffee' | 'palm' | 'dream' | 'djinn' | 'nene'

/**
 * Чем человек «платит» за предсказание — тем и различаются экраны ввода:
 * `photo` просит снимок, `text` — текст, `persona` не просит ничего.
 */
export type FormatKind = 'photo' | 'text' | 'persona'

export interface FormatDef {
  id: FormatId
  kind: FormatKind
  sym: string
  ru: string
  az: string
  en: string
  tr: string
  ruD: string
  azD: string
  enD: string
  trD: string
  /** Стоимость в звёздах. Бесплатных форматов здесь нет — см. шапку файла. */
  cost: number
  available: boolean
  /**
   * Сколько раз человек может обратиться к персонажу за один оплаченный
   * разговор. Только у `persona`.
   *
   * Потолок диегетический: у джинна это три желания, у нэнэ — разговор за
   * одну чашку чая. Персонаж договаривает и уходит сам, поэтому конец
   * читается как часть образа, а не как «сообщения кончились». Заодно это
   * держит расход токенов предсказуемым: суточная квота у Groq общая на всех.
   */
  turns?: number
}

export const FORMATS: FormatDef[] = [
  {
    id: 'coffee', kind: 'photo', sym: '❦',
    ru: 'Кофейная гуща', az: 'Qəhvə falı', en: 'Coffee reading', tr: 'Kahve falı',
    ruD: 'Узор гущи на дне чашки', azD: 'Fincanın dibindəki naxış',
    enD: 'The pattern in your cup', trD: 'Fincanın dibindeki desen',
    cost: 40, available: true,
  },
  {
    id: 'palm', kind: 'photo', sym: '≋',
    ru: 'Хиромантия', az: 'Əl falı', en: 'Palmistry', tr: 'El falı',
    ruD: 'Линии вашей ладони', azD: 'Ovcunuzun xətləri',
    enD: 'The lines of your palm', trD: 'Avucunuzun çizgileri',
    cost: 40, available: true,
  },
  {
    id: 'dream', kind: 'text', sym: '❋',
    ru: 'Толкование снов', az: 'Yuxu yozumu', en: 'Dream reading', tr: 'Rüya yorumu',
    ruD: 'Что говорит ваш сон, и дневник', azD: 'Yuxunuz nə deyir və gündəlik',
    enD: 'What your dream means, and a journal', trD: 'Rüyanız ne anlatıyor ve günlük',
    cost: 30, available: true,
  },
  {
    id: 'djinn', kind: 'persona', sym: '۞',
    ru: 'Джинн из лампы', az: 'Lampadakı Cin', en: 'The Djinn', tr: 'Lamba Cini',
    ruD: 'Три желания и толика юмора', azD: 'Üç arzu və bir az yumor',
    enD: 'Three wishes and a wink', trD: 'Üç dilek ve biraz mizah',
    cost: 60, available: true, turns: 3,
  },
  {
    id: 'nene', kind: 'persona', sym: '✾',
    ru: 'Гадалка-нэнэ', az: 'Fal açan nənə', en: 'Grandmother’s reading', tr: 'Fal bakan nine',
    ruD: 'Разговор за чашкой чая', azD: 'Bir stəkan çay söhbəti',
    enD: 'A talk over a cup of tea', trD: 'Bir bardak çay sohbeti',
    cost: 50, available: true, turns: 5,
  },
]

export function formatById(id: FormatId): FormatDef | undefined {
  return FORMATS.find((f) => f.id === id)
}

export function formatName(f: FormatDef, lang: Lang): string {
  return loc(f, lang)
}

export function formatDesc(f: FormatDef, lang: Lang): string {
  return loc({ ru: f.ruD, az: f.azD, en: f.enD, tr: f.trD }, lang)
}

/**
 * Секции каталога «Гадания».
 *
 * Пятой вкладки не заводим: подписи вкладок на турецком и азербайджанском
 * длиннее русских в полтора раза, и пятая уже не влезает в ряд. Растёт
 * каталог, а не навигация.
 */
export interface CatalogSection {
  id: 'tarot' | 'photo' | 'word' | 'persona'
  title: Loc
  /** Форматы секции; у секции Таро их нет — там расклады. */
  formats: FormatId[]
}

export const CATALOG_SECTIONS: CatalogSection[] = [
  {
    id: 'tarot',
    title: { ru: 'Таро', az: 'Taro', en: 'Tarot', tr: 'Tarot' },
    formats: [],
  },
  {
    id: 'photo',
    title: { ru: 'По фото', az: 'Şəkil ilə', en: 'By photo', tr: 'Fotoğrafla' },
    formats: ['coffee', 'palm'],
  },
  {
    id: 'word',
    title: { ru: 'Словом', az: 'Sözlə', en: 'In words', tr: 'Sözle' },
    formats: ['dream'],
  },
  {
    id: 'persona',
    title: { ru: 'Персонажи', az: 'Obrazlar', en: 'Characters', tr: 'Karakterler' },
    formats: ['djinn', 'nene'],
  },
]

/**
 * Подсказки к съёмке.
 *
 * Живут рядом с форматом, а не в словаре интерфейса: они часть самого гадания
 * (модель разбирает ровно то, что человек снял по этим правилам), и от них же
 * зависит текст экрана ошибки «фото не распознано» — он повторяет их списком.
 */
export const SHOOT_TIPS: Record<'coffee' | 'palm', Loc[]> = {
  coffee: [
    { ru: 'Дневной свет, без вспышки', az: 'Gündüz işığı, fləşsiz',
      en: 'Daylight, no flash', tr: 'Gün ışığı, flaşsız' },
    { ru: 'Вся чашка помещается в кадр', az: 'Bütün fincan kadra sığsın',
      en: 'The whole cup fits in the frame', tr: 'Fincanın tamamı kareye sığsın' },
    { ru: 'Наведите резкость на узор гущи', az: 'Naxışa fokuslanın',
      en: 'Focus on the pattern of the grounds', tr: 'Telvenin desenine odaklanın' },
  ],
  palm: [
    { ru: 'Мягкий рассеянный свет', az: 'Yumşaq, dağınıq işıq',
      en: 'Soft, diffused light', tr: 'Yumuşak, dağınık ışık' },
    { ru: 'Ладонь заполняет кадр', az: 'Ovuc kadrı doldursun',
      en: 'The palm fills the frame', tr: 'Avuç kareyi doldursun' },
    { ru: 'Держите руку неподвижно', az: 'Əli tərpətməyin',
      en: 'Hold your hand still', tr: 'Elinizi sabit tutun' },
  ],
}

/** Что написано на экране-ритуале перед съёмкой. */
export const SHOOT_INTRO: Record<'coffee' | 'palm', Loc> = {
  coffee: {
    ru: 'Допейте кофе, оставьте гущу, накройте чашку блюдцем, переверните и дайте остыть. Затем снимите дно.',
    az: 'Qəhvəni için, dibini saxlayın, fincanı nəlbəki ilə örtüb çevirin, soyumasını gözləyin. Sonra dibini çəkin.',
    en: 'Finish your coffee, leave the grounds, cover the cup with a saucer, turn it over and let it cool. Then photograph the bottom.',
    tr: 'Kahvenizi bitirin, telveyi bırakın, fincanı tabakla kapatıp ters çevirin ve soğumasını bekleyin. Sonra dibini çekin.',
  },
  palm: {
    ru: 'Сфотографируйте раскрытую ладонь рабочей руки при мягком свете. Пальцы слегка разведите — линии должны быть видны.',
    az: 'İşlək əlinizin açıq ovcunu yumşaq işıqda çəkin. Barmaqları bir az aralayın — xətlər görünsün.',
    en: 'Photograph the open palm of your working hand in soft light. Spread the fingers a little so the lines are visible.',
    tr: 'Kullandığınız elin açık avucunu yumuşak ışıkta çekin. Parmakları hafifçe açın — çizgiler görünsün.',
  },
}

/**
 * Линии ладони — фиксированный порядок.
 *
 * Модель обязана разобрать все четыре и именно в этом порядке: номера
 * подписей совпадают с номерами точек, которые интерфейс рисует поверх
 * снимка ладони. Перепутанный порядок разошёлся бы с картинкой.
 */
export const PALM_LINES: Loc[] = [
  { ru: 'Линия жизни', az: 'Həyat xətti', en: 'Life line', tr: 'Yaşam çizgisi' },
  { ru: 'Линия сердца', az: 'Ürək xətti', en: 'Heart line', tr: 'Kalp çizgisi' },
  { ru: 'Линия ума', az: 'Ağıl xətti', en: 'Head line', tr: 'Akıl çizgisi' },
  { ru: 'Линия судьбы', az: 'Tale xətti', en: 'Fate line', tr: 'Kader çizgisi' },
]

/**
 * Куда интерфейс ставит номера линий поверх снимка ладони — доли от размера
 * карточки, а не пиксели: снимок показывается разного размера.
 */
export const PALM_MARKERS = [
  { x: '20%', y: '72%' },
  { x: '32%', y: '32%' },
  { x: '24%', y: '52%' },
  { x: '58%', y: '60%' },
]

/**
 * Формат обращения к персонажу — подпись под портретом.
 *
 * У джинна это три желания (диалог), у нэнэ — одно предсказание: бабушка,
 * которая торгуется за желания, перестаёт быть бабушкой.
 */
/**
 * Заголовок готового гадания.
 *
 * Отличается от названия формата: в каталоге человек выбирает «Кофейную гущу»,
 * а над результатом должно стоять «Узор вашей чашки» — то, что он получил,
 * а не то, что он выбрал. У сна заголовок пишет модель (его же видно в
 * дневнике), поэтому здесь он запасной.
 */
export const RESULT_TITLE: Record<FormatId, Loc> = {
  coffee: { ru: 'Узор вашей чашки', az: 'Fincanınızın naxışı',
            en: 'The pattern in your cup', tr: 'Fincanınızın deseni' },
  palm: { ru: 'Линии вашей ладони', az: 'Ovcunuzun xətləri',
          en: 'The lines of your palm', tr: 'Avucunuzun çizgileri' },
  dream: { ru: 'Что говорит ваш сон', az: 'Yuxunuz nə deyir',
           en: 'What your dream says', tr: 'Rüyanız ne anlatıyor' },
  djinn: { ru: 'Джинн из лампы', az: 'Lampadakı Cin', en: 'The Djinn', tr: 'Lamba Cini' },
  nene: { ru: 'Гадалка-нэнэ', az: 'Fal açan nənə',
          en: 'Grandmother’s reading', tr: 'Fal bakan nine' },
}

export const PERSONA_FORMAT: Record<'djinn' | 'nene', Loc> = {
  djinn: { ru: 'Три желания', az: 'Üç arzu', en: 'Three wishes', tr: 'Üç dilek' },
  nene: { ru: 'Разговор за чаем', az: 'Çay söhbəti', en: 'A talk over tea', tr: 'Çay sohbeti' },
}

export const PERSONA_CAPTION: Record<'djinn' | 'nene', Loc> = {
  djinn: {
    ru: 'Загадайте три желания. Джинн отвечает с усмешкой — но каждое слово по делу.',
    az: 'Üç arzu tutun. Cin gülümsəyərək cavab verir — amma hər sözü yerindədir.',
    en: 'Make three wishes. The Djinn answers with a smirk — but every word lands.',
    tr: 'Üç dilek tutun. Cin sırıtarak yanıtlar — ama her sözü yerini bulur.',
  },
  nene: {
    ru: 'Нэнэ нальёт чаю и скажет как есть. Спрашивайте, пока чай не остыл.',
    az: 'Nənə çay süzüb düzünü deyəcək. Çay soyuyana qədər soruşun.',
    en: 'Grandmother pours the tea and says it plainly. Ask while the tea is hot.',
    tr: 'Nine çayı doldurur ve olduğu gibi söyler. Çay soğumadan sorun.',
  },
}

/**
 * Черта под разговором, когда ходы вышли.
 *
 * Ставит интерфейс, а не модель: конец разговора должен выглядеть одинаково
 * каждый раз, иначе его не отличить от поломки. Прощается при этом сам
 * персонаж — последней репликой, — поэтому здесь не второе «до свидания», а
 * приглашение вернуться: повторять за моделью то же самое было бы вяло.
 */
export const PERSONA_FAREWELL: Record<'djinn' | 'nene', Loc> = {
  djinn: {
    ru: 'Три желания исполнены. Лампу можно потереть снова.',
    az: 'Üç arzu yerinə yetdi. Lampanı yenidən sürtmək olar.',
    en: 'Three wishes granted. The lamp can be rubbed again.',
    tr: 'Üç dilek yerine geldi. Lamba yeniden ovuşturulabilir.',
  },
  nene: {
    ru: 'Чай допит. Приходите ещё — нэнэ заварит свежий.',
    az: 'Çay içilib qurtardı. Yenə gəlin — nənə təzəsini dəmləyər.',
    en: 'The tea is finished. Come again — grandmother will brew a fresh pot.',
    tr: 'Çay bitti. Yine gelin — nine tazesini demler.',
  },
}
